import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useHasRole, useIsLoggedIn } from '@/hooks/auth';
import { log } from '@/lib/utils';
import type { ReactNode } from 'react';

// Constants for timer durations
const POST_UPGRADE_DELAY = 0 * 1000; // 2 minutes
const RETURNING_USER_DELAY = 0 * 1000; // 10 seconds

interface ReferralAlertsContextType {
  clearReferralAlerts: () => void;
  isUpgradeFlow: boolean;
  showReferralAlerts: boolean;
  triggerReferralAlerts: () => void;
}
export const ReferralAlertsContext = createContext<ReferralAlertsContextType | undefined>(undefined);
interface ReferralAlertsProviderProps {
  children: ReactNode;
}
export const ReferralAlertsProvider = ({
  children
}: ReferralAlertsProviderProps) => {
  // Get user auth state
  const isLoggedIn = useIsLoggedIn();
  const isFree = useHasRole('Free');

  // Track if alert has been shown - only set to false on premium upgrade
  const [hasShownAlert, setHasShownAlert] = useLocalStorage<boolean>('referralAlertsShown', false);

  // Track if alerts should be shown
  const [showReferralAlerts, setShowReferralAlerts] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const stored = localStorage.getItem('showReferralAlerts');
    return stored ? JSON.parse(stored) as boolean : false;
  });

  // Update localStorage when showReferralAlerts changes
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem('showReferralAlerts', JSON.stringify(showReferralAlerts));
  }, [showReferralAlerts]);

  // Track previous login state using ref
  const wasLoggedInRef = useRef<boolean>(false);
  log('isLoggedIn', isLoggedIn);
  // Check stored state on login
  log('wasLoggedInRef.current', wasLoggedInRef.current);
  log('isFree', isFree);
  useEffect(() => {
    // Handle timer logic
    if (isLoggedIn && !wasLoggedInRef.current && !isFree) {
      // Only start timer if hasShownAlert is explicitly false
      if (hasShownAlert === false) {
        const isWorking = true;
        log('isWorking', isWorking);
        const handleTimerCompletion = () => {
          setShowReferralAlerts(true);
          setHasShownAlert(true);
        };
        const timer = setTimeout(() => {
          handleTimerCompletion();
        }, RETURNING_USER_DELAY);
        return () => {
          clearTimeout(timer);
        };
      }
    }

    // Update ref after checking
    wasLoggedInRef.current = isLoggedIn;
    return () => {};
  }, [isLoggedIn, isFree, hasShownAlert, setHasShownAlert]);

  // Flag to track when an upgrade flow is in progress
  const [isUpgradeFlow, setIsUpgradeFlow] = useState<boolean>(false);

  // Handle post-upgrade scenario
  const triggerReferralAlerts = useCallback(() => {
    log('Referral alerts triggered');

    // Set the upgrade flow flag to prevent free popup
    setIsUpgradeFlow(true);
    setShowReferralAlerts(false);
    setHasShownAlert(false);
    const handleTimerCompletion = () => {
      setShowReferralAlerts(true);
      setHasShownAlert(true);
      // Reset upgrade flow flag after showing alerts
      setIsUpgradeFlow(false);
      log('Referral alerts shown');
    };
    const timer = setTimeout(() => {
      handleTimerCompletion();
    }, POST_UPGRADE_DELAY);
    return () => {
      log('Referral alerts timer cleared');
      clearTimeout(timer);
    };
  }, [setHasShownAlert]);

  // Clear alerts and reset state
  const clearReferralAlerts = useCallback(() => {
    log('Clearing referral alerts');
    setShowReferralAlerts(false);
    setHasShownAlert(false);
  }, [setHasShownAlert]);

  // Create memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    showReferralAlerts,
    triggerReferralAlerts,
    clearReferralAlerts,
    isUpgradeFlow
  }), [showReferralAlerts, triggerReferralAlerts, clearReferralAlerts, isUpgradeFlow]);
  return <ReferralAlertsContext.Provider value={contextValue} data-sentry-element="ReferralAlertsContext.Provider" data-sentry-component="ReferralAlertsProvider" data-sentry-source-file="ReferralAlertsProvider.tsx">
      {children}
    </ReferralAlertsContext.Provider>;
};
export const useReferralAlerts = () => {
  const context = useContext(ReferralAlertsContext);
  if (context === undefined) {
    throw new Error('useReferralAlerts must be used within a ReferralAlertsProvider');
  }
  return context;
};